import 'react-toastify/dist/ReactToastify.css';

import ScreenWrapper from '@components/layout/screen-wrapper';
import {
  BrandLogo,
  Button,
  Field,
  Input,
  Stack,
  Text,
} from '@components/ui';
import {
  phoneDigits,
  toSubmittedPhoneNumber,
} from '@features/auth/phoneNumber';
import {
  type LoginFormValues,
  loginSchema,
} from '@features/auth/schemas/loginSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLoginMutation } from '@lib/api/auth';
import { errorBody } from '@lib/api/http';
import { persistUser, setLoggedIn } from '@lib/auth/session';
import { AssignmentOutlined, PersonOutline } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import type { SxProps, Theme } from '@mui/material/styles';
import type { AuthLocationState, LoginResponse } from '@typings/auth';
import { type ReactNode, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AuthSplitCard } from './auth-split-card';


const authPageSx = {
  height: '100dvh',
  maxHeight: '100dvh',
  width: '100%',
  overflow: 'auto',
};

const authContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  height: 'auto',
  width: '100%',
};

const roleTabSx = (active: boolean): SxProps<Theme> => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1,
  p: 2,
  cursor: 'pointer',
  borderRadius: '12px',
  border: '1px solid',
  borderColor: active ? 'primary.main' : 'transparent',
  bgcolor: active
    ? 'background.paper'
    : (theme) => (theme.palette.mode === 'dark' ? '#262a2e' : '#f7f8fa'),
  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
  color: 'text.primary',
  font: 'inherit',
  transition: 'all 0.2s',
});

interface RoleTabProps {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  title: string;
  subtitle: string;
}

const RoleTab = ({
  active,
  onClick,
  icon,
  title,
  subtitle,
}: RoleTabProps): JSX.Element => (
  <Grid item xs={6}>
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={roleTabSx(active)}
    >
      {icon}
      <Text as="span" variant="label" sx={{ fontWeight: 700 }}>
        {title}
      </Text>
      <Text as="span" variant="caption" tone="muted">
        {subtitle}
      </Text>
    </Box>
  </Grid>
);

const LogIn = (): JSX.Element => {
  const { t } = useTranslation();
  const url = new URL(window.location.href).searchParams;
  const itemId = url.get('itemID');

  const navigator = useNavigate();
  const location = useLocation();
  const loginMutation = useLoginMutation();
  const loginLoading = loginMutation.isPending;
  const [userRole, setUserRole] = useState<'buyer' | 'r8tr'>('r8tr');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem('rememberMe') === 'true';
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitted, isValid },
  } = useForm<LoginFormValues>({
    defaultValues: {
      phoneNumber: '',
      password: '',
    },
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    const state = location?.state as AuthLocationState | undefined;
    if (state?.userId) {
      navigator('/signup', { state: { userId: state.userId } });
    }
  }, [location?.state, navigator]);

  useEffect(() => {
    localStorage.setItem('rememberMe', rememberMe.toString());
  }, [rememberMe]);

  const handleLoginFormData = (data: LoginResponse): void => {
    if (data?.status && data.data) {
      persistUser(data.data);
      localStorage.setItem('userRole', userRole);
      setLoggedIn(true);

      if (itemId) {
        navigator(`/ebay-hire-a-r8tr?itemID=${itemId}`);
      } else {
        navigator('/');
      }
    } else {
      toast.error(
        data?.message || 'An error occurred. Please try again.'
      );
    }
  };

  const onSubmit = (values: LoginFormValues): void => {
    const fullPhoneNumber = toSubmittedPhoneNumber(values.phoneNumber);
    loginMutation.mutate(
      { phoneNumber: fullPhoneNumber, password: values.password },
      {
        onSuccess: (data) => handleLoginFormData(data as LoginResponse),
        onError: (error) =>
          handleLoginFormData(errorBody(error) as LoginResponse),
      }
    );
  };

  const showPhoneError =
    (isSubmitted || errors.phoneNumber) && errors.phoneNumber;
  const showPasswordError = (isSubmitted || errors.password) && errors.password;

  return (
    <ScreenWrapper pageSx={authPageSx} containerSx={authContainerSx}>
      <Box
        sx={{
          m: 'auto',
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          py: 3,
        }}
      >
        <AuthSplitCard>
          <BrandLogo
            alt={t('common.logoAlt')}
            height={48}
            sx={{ mx: 'auto', mb: 1.5 }}
          />
          <Text
            variant="subtitle"
            align="center"
            sx={{ mb: 1, fontWeight: 600, display: { md: 'none' } }}
          >
            {t('auth.tagline')}
          </Text>
          <Text
            variant="body"
            tone="muted"
            align="center"
            sx={{ mb: 3.5, fontSize: '0.875rem', display: { md: 'none' } }}
          >
            {t('auth.description')}
          </Text>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}
          >
            {!itemId && (
              <Grid container spacing={1.5} sx={{ mb: 3.5 }}>
                <RoleTab
                  active={userRole === 'r8tr'}
                  onClick={() => setUserRole('r8tr')}
                  icon={
                    <AssignmentOutlined
                      fontSize="small"
                      sx={{
                        color:
                          userRole === 'r8tr'
                            ? 'primary.main'
                            : 'text.secondary',
                      }}
                    />
                  }
                  title={t('common.r8tr')}
                  subtitle={t('auth.inspectors')}
                />
                <RoleTab
                  active={userRole === 'buyer'}
                  onClick={() => setUserRole('buyer')}
                  icon={
                    <PersonOutline
                      fontSize="small"
                      sx={{
                        color:
                          userRole === 'buyer'
                            ? 'primary.main'
                            : 'text.secondary',
                      }}
                    />
                  }
                  title={t('common.buyer')}
                  subtitle={t('auth.shoppers')}
                />
              </Grid>
            )}

            <Field
              sx={{ mb: 1 }}
              label={t('auth.mobileNumber')}
              error={
                showPhoneError
                  ? t(errors.phoneNumber?.message ?? '')
                  : undefined
              }
            >
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    type="tel"
                    id="phone-number"
                    invalid={Boolean(showPhoneError)}
                    placeholder={t('common.phonePlaceholder')}
                    inputProps={{ maxLength: 10 }}
                    value={phoneDigits(field.value)}
                    onChange={(event) => {
                      field.onChange(phoneDigits(event.target.value));
                    }}
                    onBlur={field.onBlur}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {t('common.countryCode')}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Field>

            <Field
              sx={{ mb: 1 }}
              label={t('auth.password')}
              error={
                showPasswordError
                  ? t(errors.password?.message ?? '')
                  : undefined
              }
            >
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                invalid={Boolean(showPasswordError)}
                placeholder={t('auth.passwordPlaceholder')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        edge="end"
                        aria-label={t('common.togglePassword')}
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'text.primary' },
                        }}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...register('password')}
              />
            </Field>

            <Stack
              direction="row"
              align="center"
              justify="between"
              sx={{ mb: 3 }}
            >
              <FormControlLabel
                sx={{
                  m: 0,
                  gap: 1,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: 'text.primary',
                  },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                    sx={{
                      p: 0.5,
                      color: 'text.secondary',
                      '&.Mui-checked': { color: 'primary.main' },
                    }}
                  />
                }
                label={t('auth.rememberMe')}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigator(itemId ? `/get-otp?itemID=${itemId}` : '/get-otp', {
                    state: { userRole },
                  })
                }
                sx={{ minWidth: 0, px: 0 }}
              >
                {t('auth.forgotPassword')}
              </Button>
            </Stack>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={!isValid}
              loading={loginLoading}
            >
              {t('auth.signIn')}
            </Button>

            <Stack
              align="center"
              gap="md"
              sx={{ mt: 'auto', pt: 5, textAlign: 'center' }}
            >
              <Text
                variant="body"
                tone="muted"
                sx={{
                  fontSize: '0.875rem',
                  '& strong': { color: 'text.primary' },
                }}
              >
                <Trans
                  i18nKey="auth.createPrompt"
                  components={{ strong: <strong /> }}
                />
              </Text>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                onClick={() =>
                  navigator(itemId ? `/signup?itemID=${itemId}` : '/signup')
                }
              >
                {t('auth.createAccount')}
              </Button>
            </Stack>
          </Box>
        </AuthSplitCard>
      </Box>
    </ScreenWrapper>
  );
};
export default LogIn;
